/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import { useGridApiRef } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import ManufacturingSearchBox from '../../components/manufacturingmng/ManufacturingSearchBox';
import ManufacturingStatusChips from '../../components/manufacturingmng/ManufacturingStatusChips';
import ProductionPlanAddDialog from '../../components/manufacturingmng/productionplan/ProductionPlanAddDialog';
import * as manufacturingApi from '../../lib/api/manufacturing';

/**
 * 프로젝트별 생산 계획 목록 컨테이너
 * - /manufacturingmng/:projectId/plans
 * - 특정 프로젝트의 생산 계획 목록 표시
 */
const ProjectPlansContainer = ({ projectId, pageUrl }) => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  
  // 상태 관리
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    totalCount: 0,
  });
  
  // 필터 기본값
  const [filters, setFilters] = useState({
    statusFilter: '',
    manufacturingStatus: '',
    searchKeyword: 'planNumber',
    searchWords: '',
    searchStrtDt: dayjs().startOf('year'),
    searchEndDt: dayjs().endOf('year'),
  });

  // ===========================================
  // API 호출: 프로젝트별 생산 계획 목록 조회
  // ===========================================
  const fetchProjectPlans = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('프로젝트별 생산 계획 조회: projectId =', projectId);
      const response = await manufacturingApi.getProjectPlans(projectId);
      console.log('API 응답:', response.data);
      
      if (response.data.success) {
        const { content, totalElements, projectInfo: pInfo } = response.data.data;
        
        // 프로젝트 정보 저장
        if (pInfo) {
          setProjectInfo(pInfo);
        }
        
        const uniqueContent = Array.from(
          new Map(
            (content || []).map((item, index) => [
              item.planId || `row-${index}`,
              item,
            ]),
          ).values(),
        );

        // 데이터 변환
        const transformedData = uniqueContent.map((item, index) => ({
          id: item.planId || `row-${index}`,
          planId: item.planId,
          planNumber: item.planNumber,
          projectId: item.projectId,
          projectNumber: item.projectNumber || projectId,
          projectName: item.projectName || '',
          planQty: item.planQty || null,
          planStartDate: item.planStartDate || '',
          planEndDate: item.planEndDate || '',
          status: item.status,
          statusName: item.statusName || getStatusLabel(item.status),
          manufacturingStatus: getStatusLabel(item.status),
          progress: calculateProgress(item.status),
          manager: item.createdByName || '',
          createdDate: item.createdAt?.split(' ')[0] || '',
          remarks: item.remarks || '',
          // 생산이 끝난 뒤에 붙인 계획인지, 그 사유는 무엇인지
          extraYn: item.extraYn || 'N',
          addReason: item.addReason || '',
        }));
        
        setPlans(transformedData);
        setPagination(prev => ({
          ...prev,
          totalCount: Math.min(totalElements || transformedData.length, transformedData.length),
        }));
      }
    } catch (err) {
      console.error('API 에러:', err);
      
      // API 에러 시 Mock 데이터로 폴백
      if (err.response?.status === 404 || err.code === 'ERR_NETWORK') {
        console.log('API 미구현 또는 네트워크 에러 - Mock 데이터 사용');
        loadMockData();
        setError('서버 연결 실패 - Mock 데이터를 표시합니다.');
      } else {
        setError(err.response?.data?.message || '데이터를 불러오는데 실패했습니다.');
        setPlans([]);
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // ===========================================
  // Mock 데이터
  // ===========================================
  const loadMockData = () => {
    setProjectInfo({
      projectId: projectId,
      projectNumber: 'HC_PJ_2025_02_025',
      projectName: '[조달] 평택시청 행정정보망 ESA(내PC지키미) 구매',
      customerName: '(주)유앤아이소프트',
    });
    
    const mockData = [
      {
        id: 'plan-uuid-1',
        planId: 'plan-uuid-1',
        planNumber: 'MANU-20251127-0001',
        projectId: projectId,
        projectNumber: 'HC_PJ_2025_02_025',
        projectName: '[조달] 평택시청 행정정보망 ESA(내PC지키미) 구매',
        planQty: 100,
        planStartDate: '2025-02-18',
        planEndDate: '2025-02-28',
        status: 'IN_PROGRESS',
        statusName: '진행중',
        manufacturingStatus: '진행중',
        progress: 50,
        manager: '김설계',
        createdDate: '2025-02-18',
        remarks: '1차 생산 계획',
      },
      {
        id: 'plan-uuid-2',
        planId: 'plan-uuid-2',
        planNumber: 'MANU-20251127-0002',
        projectId: projectId,
        projectNumber: 'HC_PJ_2025_02_025',
        projectName: '[조달] 평택시청 행정정보망 ESA(내PC지키미) 구매',
        planQty: 50,
        planStartDate: '2025-03-01',
        planEndDate: '2025-03-15',
        status: 'PLAN',
        statusName: '설계전',
        manufacturingStatus: '설계전',
        progress: 0,
        manager: '',
        createdDate: '2025-02-20',
        remarks: '2차 생산 계획',
      },
    ];
    
    setPlans(mockData);
    setPagination(prev => ({
      ...prev,
      totalCount: mockData.length,
    }));
  };

  // 상태 라벨 변환
  const getStatusLabel = (status) => {
    // 공통코드(MANU/PLAN_STATUS)에 있는 값을 여기에도 적어둔다. 빠진 값은 코드 그대로
    // 화면에 나온다 — PRODUCED, QC_FAILED 가 실제로 그렇게 나오고 있었다.
    const statusMap = {
      'PLAN': '설계전',
      'IN_DESIGN': '설계중',
      'IN_PRODUCTION': '생산중',
      'IN_PROGRESS': '진행중',
      'IN_QC': 'QC검사중',
      'PRODUCED': '생산완료',
      'QC_FAILED': '품질불합격',
      'SHIPPED': '출하완료',
      'COMPLETED': '완료',
      'CANCELLED': '취소',
      'CLOSED': '종결',
    };
    return statusMap[status] || status || '설계전';
  };

  // 진행률 계산
  const calculateProgress = (status) => {
    const progressMap = {
      'PLAN': 0,
      'IN_PROGRESS': 50,
      'COMPLETED': 100,
      'CANCELLED': 0,
    };
    return progressMap[status] || 0;
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchProjectPlans();
  }, [projectId]);

  // ===========================================
  // 통계 계산
  // ===========================================
  const statusCounts = useMemo(() => {
    const counts = {
      '전체': plans.length,
      '미시작': 0,
      '설계중': 0,
      '자재준비중': 0,
      '생산중': 0,
      '품질검사중': 0,
      '완료': 0,
      '종결': 0,
    };
    
    // 기존 상태 매핑
    plans.forEach(row => {
      const status = row.manufacturingStatus;
      if (status === '설계전') counts['미시작']++;
      else if (status === '진행중') counts['생산중']++;
      else if (status === '종결') counts['종결']++;
      else if (counts[status] !== undefined) counts[status]++;
    });
    
    return counts;
  }, [plans]);

  // ===========================================
  // 컬럼 정의
  // ===========================================
  const columns = useMemo(
    () => [
      {
        field: 'planNumber',
        headerName: '생산계획번호',
        width: 220,
        align: 'center',
        headerAlign: 'center',
        // 생산이 끝난 뒤에 추가된 계획은 번호 옆에 표시한다.
        // 사유는 마우스를 올리면 나온다 — 목록에 다 펼치면 자리를 너무 먹는다.
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
            <span>{params.value}</span>
            {params.row?.extraYn === 'Y' && (
              <Tooltip title={params.row?.addReason || '사유 없음'}>
                <Chip label="추가" size="small" color="warning" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'planQty',
        headerName: '목표수량',
        width: 100,
        align: 'right',
        headerAlign: 'center',
        renderCell: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === '') {
            return '-';
          }
          return new Intl.NumberFormat('ko-KR').format(value);
        },
      },
      {
        field: 'planStartDate',
        headerName: '시작일',
        width: 120,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'planEndDate',
        headerName: '종료일',
        width: 120,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'manufacturingStatus',
        headerName: '상태',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const getStatusColor = () => {
            switch (params.value) {
              case '설계전': return 'info';
              case '진행중': return 'warning';
              case '완료': return 'success';
              case '취소': return 'error';
              case '종결': return 'default';
              default: return 'default';
            }
          };
          
          return (
            <Chip 
              label={params.value} 
              size="small" 
              color={getStatusColor()}
              variant={params.value === '종결' ? 'outlined' : 'filled'}
              sx={params.value === '종결' ? { textDecoration: 'line-through', color: '#999' } : {}}
            />
          );
        },
      },
      {
        field: 'manager',
        headerName: '담당자',
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'createdDate',
        headerName: '생성일',
        width: 120,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'remarks',
        headerName: '비고',
        minWidth: 200,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'actions',
        headerName: '작업',
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDetailClick(params.row);
            }}
          >
            상세
          </Button>
        ),
      },
    ],
    [],
  );

  // ===========================================
  // 이벤트 핸들러
  // ===========================================
  
  // 뒤로가기
  const handleBack = () => {
    navigate(pageUrl);
  };

  // 상세 버튼 클릭
  const handleDetailClick = (row) => {
    navigate(`${pageUrl}/${projectId}/plans/${row.planId}`);
  };

  // 행 클릭
  const handleRowClick = useCallback((param, event) => {
    navigate(`${pageUrl}/${projectId}/plans/${param.row.planId}`);
  }, [navigate, pageUrl, projectId]);

  // 필터 변경
  const handleFilters = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // 페이지네이션 변경
  const handlePaginationModelChange = useCallback((newModel) => {
    setPagination(prev => ({
      ...prev,
      page: newModel.page,
      pageSize: newModel.pageSize,
    }));
  }, []);

  // 모달 열기/닫기
  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  // 생산 계획 저장
  const handleSavePlan = async (planData) => {
    setCreateLoading(true);
    try {
      console.log('=== handleSavePlan: planData ===', planData);
      
      const createData = {
        projectId: projectId,
        // 제품 정보
        productId: planData.productUuid || null,
        productName: planData.productName || null,
        cavity: planData.cavity || null,
        // 수량/일정
        planQty: planData.planQty ? parseInt(planData.planQty, 10) : null,
        targetQuantity: planData.planQty ? parseInt(planData.planQty, 10) : null,
        planStartDate: planData.planStartDate || dayjs().format('YYYY-MM-DD'),
        planEndDate: planData.expectedCompletionDate || dayjs().add(1, 'month').format('YYYY-MM-DD'),
        // 담당자 정보
        designManagerUuid: planData.designerUuid || null,
        designManagerName: planData.designerName || null,
        productionManagerUuid: planData.productionManagerUuid || null,
        productionManagerName: planData.productionManagerName || null,
        qcManagerUuid: planData.qcManagerUuid || null,
        qcManagerName: planData.qcManagerName || null,
        // 워크플로우
        workflowUuid: planData.workflowUuid || null,
        // 기타
        remarks: planData.remarks || null,
        addReason: planData.addReason || null,
      };
      
      console.log('생산 계획 생성 요청:', createData);
      const response = await manufacturingApi.createManufacturingPlan(createData);
      
      if (response.data.success) {
        const savedPlanId = response.data.data?.planId || response.data.data?.planUuid;
        
        // 워크플로우 기반 라우팅 생성
        if (planData.workflowUuid && savedPlanId) {
          try {
            console.log('=== 라우팅 생성 API 호출 ===');
            const routingData = {
              planId: savedPlanId,
              workflowUuid: planData.workflowUuid,
              productUuid: planData.productUuid || null,
            };
            console.log('routingData:', routingData);
            const routingResponse = await manufacturingApi.createRoutingFromWorkflow(routingData);
            console.log('라우팅 생성 응답:', routingResponse.data);
          } catch (routingError) {
            console.error('라우팅 생성 실패:', routingError);
            // 라우팅 생성 실패는 경고만 표시
            alert('생산계획은 생성되었으나, 워크플로우 라우팅 생성에 실패했습니다. 상세 화면에서 수정해주세요.');
          }
        }
        
        alert('생산 계획이 생성되었습니다.');
        handleCloseCreateModal();
        fetchProjectPlans();
      }
    } catch (err) {
      console.error('생산 계획 생성 에러:', err);
      alert(err.response?.data?.message || '생산 계획 생성에 실패했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  // ===========================================
  // 생산 계획 삭제
  // ===========================================
  const handleDeleteClick = () => {
    if (selectedRows.length === 0) {
      alert('삭제할 생산 계획을 선택해주세요.');
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setError(null);
    
    try {
      const deletePromises = selectedRows.map(planId => 
        manufacturingApi.deleteManufacturingPlan(planId)
      );
      await Promise.all(deletePromises);
      
      alert(`${selectedRows.length}건의 생산 계획이 삭제되었습니다.`);
      setSelectedRows([]);
      setDeleteConfirmOpen(false);
      fetchProjectPlans();
    } catch (err) {
      console.error('생산 계획 삭제 에러:', err);
      setError(err.response?.data?.message || '삭제에 실패했습니다.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ===========================================
  // Formik 설정
  // ===========================================
  const validationSchema = yup.object({
    searchWords: yup.string().trim().nullable(),
    searchStrtDt: yup.date().nullable(),
    searchEndDt: yup.date().min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요').nullable(),
  });

  const formik = useFormik({
    initialValues: {
      manufacturingStatus: filters.manufacturingStatus,
      searchKeyword: filters.searchKeyword,
      searchWords: filters.searchWords,
      searchStrtDt: filters.searchStrtDt,
      searchEndDt: filters.searchEndDt,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setFilters(prev => ({
        ...prev,
        ...values,
      }));
    },
  });

  const handleReset = () => {
    setFilters({
      statusFilter: '',
      manufacturingStatus: '',
      searchKeyword: 'planNumber',
      searchWords: '',
      searchStrtDt: dayjs().startOf('year'),
      searchEndDt: dayjs().endOf('year'),
    });
    formik.resetForm();
  };

  // ===========================================
  // 렌더링
  // ===========================================
  return (
    <>
      <BreadcrumbsContainer
        title="생산/프로젝트별 제조"
        icon="production"
      />

      {/* 상단 헤더 */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              생산 계획 목록
            </Typography>
            {projectInfo && (
              <Typography variant="body2" color="text.secondary">
                {projectInfo.projectNumber} - {projectInfo.projectName}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedRows.length > 0 && (
            <Button 
              variant="outlined" 
              color="error"
              size="large"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              선택 삭제 ({selectedRows.length})
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            onClick={handleOpenCreateModal}
          >
            + 생산 계획 생성
          </Button>
        </Box>
      </Box>

      <ManufacturingSearchBox 
        formik={formik} 
        onReset={handleReset}
      />

      <ManufacturingStatusChips 
        filters={filters}
        onFilters={handleFilters}
        statusCounts={statusCounts}
      />

      {error && (
        <Box sx={{ p: 2 }}>
          <Alert 
            severity={error.includes('Mock') ? 'warning' : 'error'} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Box>
      )}

      <Box sx={{ p: 2 }}>
        <Paper elevation={1}>
          <Box sx={{ height: 500, width: '100%', position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <DataGrid
              apiRef={apiRef}
              rows={plans}
              columns={columns}
              pagination
              paginationMode="client"
              rowCount={pagination.totalCount}
              paginationModel={{
                page: pagination.page,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
              onRowClick={handleRowClick}
              loading={loading}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* 생산 계획 생성 모달 */}
      <ProductionPlanAddDialog 
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleSavePlan}
        selectedPlan={null}
        projectId={projectId}
        loading={createLoading}
      />

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>생산 계획 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            선택한 <strong>{selectedRows.length}건</strong>의 생산 계획을 삭제하시겠습니까?
            <br /><br />
            {plans
              .filter(p => selectedRows.includes(p.id))
              .map(p => (
                <Chip
                  key={p.id}
                  label={p.planNumber}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5, fontFamily: 'monospace' }}
                />
              ))
            }
            <br /><br />
            <Typography variant="caption" color="error">
              ※ 삭제된 생산 계획은 복구할 수 없습니다.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectPlansContainer;
